import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Athletes() {
  const [athletes, setAthletes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", number: "", position: "", picture: "" });
  const navigate = useNavigate();

  const load = async () => {
    const r = await api.get("/athletes");
    setAthletes(r.data);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.name) return toast.error("Nome obrigatório");
    try {
      await api.post("/athletes", {
        name: form.name,
        number: form.number ? parseInt(form.number) : null,
        position: form.position || null,
        picture: form.picture || "",
      });
      toast.success("Atleta cadastrada");
      setOpen(false);
      setForm({ name: "", number: "", position: "", picture: "" });
      load();
    } catch (e) {
      toast.error("Erro ao criar atleta");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remover ${name}? Todos os arremessos serão apagados.`)) return;
    await api.delete(`/athletes/${id}`);
    toast.success("Atleta removida");
    load();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Gestão</div>
            <h1 className="font-display font-black text-5xl uppercase tracking-tight mt-1">Atletas</h1>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-athlete-btn" className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase tracking-wide rounded-sm">
                + Nova Atleta
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase">Nova Atleta</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  data-testid="athlete-name-input"
                  placeholder="Nome"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#0A0A0A] border-zinc-800 text-white"
                />
                <Input
                  data-testid="athlete-number-input"
                  placeholder="Número (camisa)"
                  type="number"
                  value={form.number}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  className="bg-[#0A0A0A] border-zinc-800 text-white"
                />
                <Input
                  data-testid="athlete-position-input"
                  placeholder="Posição (ex.: Armadora, Pivô)"
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                  className="bg-[#0A0A0A] border-zinc-800 text-white"
                />
                <Input
                  data-testid="athlete-picture-input"
                  placeholder="URL da foto (opcional)"
                  value={form.picture}
                  onChange={(e) => setForm({ ...form, picture: e.target.value })}
                  className="bg-[#0A0A0A] border-zinc-800 text-white"
                />
              </div>
              <DialogFooter>
                <Button onClick={handleAdd} data-testid="confirm-add-athlete" className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase rounded-sm">
                  Cadastrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="athletes-grid">
          {athletes.length === 0 && (
            <div className="col-span-full text-zinc-500 text-sm p-8 border border-dashed border-zinc-800 rounded-md text-center">
              Nenhuma atleta cadastrada ainda. Clique em &quot;+ Nova Atleta&quot; para começar.
            </div>
          )}
          {athletes.map((a) => (
            <div key={a.athlete_id} className="bg-[#141414] border border-zinc-800 rounded-md p-5 hover:border-zinc-600 transition-all">
              <div className="flex items-center gap-4">
                {a.picture ? (
                  <img src={a.picture} alt="" className="w-14 h-14 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center text-xl font-display font-black">
                    {a.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{a.name}</div>
                  <div className="text-xs text-zinc-500">{a.position || "—"} {a.number ? `• #${a.number}` : ""}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button
                  onClick={() => navigate(`/atleta/${a.athlete_id}`)}
                  data-testid={`view-${a.athlete_id}`}
                  variant="outline"
                  className="bg-transparent border-zinc-700 hover:border-zinc-500 hover:bg-zinc-900 text-white text-xs uppercase font-bold rounded-sm"
                >
                  Ver Passaporte
                </Button>
                <Button
                  onClick={() => handleDelete(a.athlete_id, a.name)}
                  data-testid={`delete-${a.athlete_id}`}
                  variant="outline"
                  className="bg-transparent border-red-900/50 hover:bg-red-900/20 text-red-400 text-xs uppercase font-bold rounded-sm"
                >
                  Remover
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
